<script setup>
import { computed, ref } from 'vue';
import { Head } from '@inertiajs/vue3';
import { storeToRefs } from 'pinia';
import StatusChip from '@/components/StatusChip';
import { useBillingStore } from '@/stores/billing';

const billingStore = useBillingStore();
const { overview } = storeToRefs(billingStore);

const redirecting = ref(false);

const statusColors = {
    active: 'success',
    trialing: 'info',
    grace: 'warning',
    past_due: 'error',
    unpaid: 'error',
    incomplete: 'error',
    canceled: 'neutral',
    paused: 'neutral',
};

const status = computed(() => overview.value?.subscription?.status ?? null);
const statusColor = computed(() => statusColors[status.value] ?? 'neutral');

const canSubscribe = computed(() => {
    if (!overview.value?.plan?.synced) {
        return false;
    }
    return !status.value || ['canceled', 'incomplete'].includes(status.value);
});

const redirect = (action) => {
    redirecting.value = true;
    action()
        .then(({ data }) => {
            window.location.href = data.url;
        })
        .catch(() => {
            redirecting.value = false;
        });
};
</script>

<template>
<MainContentLayout>
    <Head :title="$t('label.billing')" />

    <template #title>{{ $t('label.billing') }}</template>

    <template v-if="overview">
        <div class="grid lg:grid-cols-3 gap-4 mb-6">
            <div class="rounded-xl border border-line bg-surface p-5">
                <p class="text-xs font-semibold uppercase tracking-wider mb-1 text-ink-light">{{ $t('label.current_plan') }}</p>
                <p class="font-display font-bold text-3xl">{{ overview.plan?.name }}</p>
                <p class="text-xs mt-1 text-ink-light">
                    {{ overview.plan?.price }} {{ $t('label.per_month') }}
                </p>
            </div>
            <div class="rounded-xl border border-line bg-surface p-5">
                <p class="text-xs font-semibold uppercase tracking-wider mb-1 text-ink-light">{{ $t('label.subscription') }}</p>
                <StatusChip
                    v-if="status"
                    :color="statusColor"
                    class="mt-1">
                    {{ $t('label.billing_status_' + status) }}
                </StatusChip>
                <p
                    v-else
                    class="font-display font-bold text-3xl text-ink-light">{{ $t('label.no_subscription') }}</p>
                <p
                    v-if="status === 'grace' && overview.subscription?.ends_at"
                    class="text-xs mt-2 text-ink-light">
                    {{ $t('label.cancels_on') }} <span class="font-mono">{{ overview.subscription.ends_at }}</span>
                </p>
                <p
                    v-if="status === 'trialing' && overview.subscription?.trial_ends_at"
                    class="text-xs mt-2 text-ink-light">
                    {{ $t('label.trial_ends') }} <span class="font-mono">{{ overview.subscription.trial_ends_at }}</span>
                </p>
            </div>
            <div class="rounded-xl border border-line bg-surface p-5">
                <p class="text-xs font-semibold uppercase tracking-wider mb-1 text-ink-light">{{ $t('label.payment_method') }}</p>
                <p
                    v-if="overview.card"
                    class="font-display font-bold text-3xl capitalize">
                    {{ overview.card.type }} <span class="font-mono text-xl">{{ '•••• ' + overview.card.last_four }}</span>
                </p>
                <p
                    v-else
                    class="font-display font-bold text-3xl text-ink-light">{{ $t('label.no_payment_method') }}</p>
            </div>
        </div>

        <div class="rounded-xl border border-line bg-surface p-5">
            <div
                v-if="!overview.plan?.synced"
                class="rounded-lg bg-warning-soft text-warning text-sm font-medium px-4 py-3 mb-4">
                {{ $t('label.plan_not_synced') }}
            </div>
            <div class="flex flex-wrap gap-3">
                <StyledButton
                    v-if="canSubscribe"
                    :disabled="redirecting"
                    @click="redirect(billingStore.startCheckout)">
                    <FontAwesomeIcon
                        icon="credit-card"
                        class="mr-1" />
                    {{ $t('label.subscribe') }}
                </StyledButton>
                <StyledButton
                    v-if="overview.has_customer"
                    :disabled="redirecting"
                    @click="redirect(billingStore.openPortal)">
                    <FontAwesomeIcon
                        icon="arrow-up-right-from-square"
                        class="mr-1" />
                    {{ $t('label.manage_billing') }}
                </StyledButton>
            </div>
            <p class="text-xs mt-3 text-ink-light">{{ $t('label.billing_portal_hint') }}</p>
        </div>
    </template>

    <div
        v-else
        class="rounded-xl border border-line bg-surface p-5">
        <p class="text-sm text-ink-light">{{ $t('label.billing_requires_dealership') }}</p>
    </div>
</MainContentLayout>
</template>
